import React from 'react'
import { IonList, IonItem, IonCol, IonRow, IonLabel } from '@ionic/react'
import { BaseSyntheticEvent } from 'react'
import { useHistory } from 'react-router'
import { TabConstants } from '../../../components/tabs/Tabs.constants'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { isTablet } from '../../../utilities'
import { setShowSingleSelectLookup } from '../../patientMedications/store/patientMedications.actions'
import { setSelectedOption as setBillingSelectedOption } from '../../billing/store/billing.actions'
import { setSelectedOption as setInitialAssessmentSelectedOption } from '../../initialAssessments/store/initialAssessment.actions'
import { getHomeSelector } from '../../home/selectors/getHome.selector'
import { BillingConstants } from '../../billing/store/billing.constants'
import { getConfigurationsSelector } from '../../configurations/selectors'
import { IConfigurationsState } from '../../configurations/store/configurations.types'
import { getPatientInformationState } from '../../patientInformation/selectors/patientInformation.selector'
import { InitialAssessmentConstants } from '../../initialAssessments/store/initialAssessment.constants'
import { setCardiacArrestDefaults } from '../../ccHpi/store/ccHpi.actions'

const PatientList: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const history = useHistory()
  const { hideComponent } = useSelector(getHomeSelector)

  const configurationsState: IConfigurationsState = useSelector(getConfigurationsSelector)
  const { serviceConfig, datapointsConfig } = configurationsState
  const { DP_PG6: fluidsPageCommandFacilityVisibility } = datapointsConfig
  const { PR_PG6: fluidsPageServiceVisibility } = serviceConfig

  const { hidePatientAndBilling } = useSelector(getPatientInformationState)

  const shouldShowFluidsPage = () => {
    const NOT_DISPLAYED = 0
    const DISPLAYED = 1
    const OPTIONAL = 2

    if (fluidsPageCommandFacilityVisibility === NOT_DISPLAYED) {
      return false
    }
    if (fluidsPageCommandFacilityVisibility === DISPLAYED) {
      return true
    }
    if (fluidsPageCommandFacilityVisibility === OPTIONAL) {
      return fluidsPageServiceVisibility === DISPLAYED
    }
    return false
  }

  const onPatientInfoRowClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    history.push(TabConstants.PATIENT_INFO_ROUTE)
    if (isTablet()) {
      dispatch(setShowSingleSelectLookup(false))
    }
  }

  const onNarrativeClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    history.push(TabConstants.PATIENT_NARRATIVE)
  }

  const onCcHpiRowClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    dispatch(setCardiacArrestDefaults())
    history.push(TabConstants.CC_HPI)
  }

  const onBillingRowClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    dispatch(setBillingSelectedOption(BillingConstants.INSURANCE_INFORMATION_OPTION))
    history.push(TabConstants.BILLING)
  }

  const onInitialAssessmentClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    dispatch(setInitialAssessmentSelectedOption(InitialAssessmentConstants.NEURO))
    history.push(TabConstants.INITIAL_ASSESSMENT)
  }

  const onAdditionalAssessmentsClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    history.push(TabConstants.ADDITIONAL_ASSESSMENTS)
  }

  const onFluidsClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    history.push(TabConstants.FLUIDS)
  }

  const showNotImplementedAlert = () => {
    alert('Not implemented')
  }

  return (
    <IonList>
      <IonItem data-testid='PatientInformation' onClick={onPatientInfoRowClick}>
        <IonCol className='ion-padding'>
          <IonRow>
            <IonLabel color='dark'>
              <h2>{t('fullChart.patient.PatientInformation')}</h2>
            </IonLabel>
          </IonRow>
          <IonRow>
            <IonLabel color='medium'>
              <h3>{t('fullChart.patient.AddressAllergiesCurrentMedsAndRelativeInfo')}</h3>
            </IonLabel>
          </IonRow>
        </IonCol>
      </IonItem>
      {!hidePatientAndBilling && (
        <IonItem onClick={onBillingRowClick}>
          <IonCol className='ion-padding'>
            <IonRow>
              <IonLabel color='dark'>
                <h2>{t('fullChart.patient.Billing')}</h2>
              </IonLabel>
            </IonRow>
            <IonRow>
              <IonLabel color='medium'>
                <h3>{t('fullChart.patient.InsuranceCMSAndOtherPaymentDetails')}</h3>
              </IonLabel>
            </IonRow>
          </IonCol>
        </IonItem>
      )}
      <IonItem onClick={onCcHpiRowClick}>
        <IonCol className='ion-padding'>
          <IonRow>
            <IonLabel color='dark'>
              <h2>{t('fullChart.patient.CCHPI')}</h2>
            </IonLabel>
          </IonRow>
          <IonRow>
            <IonLabel color='medium'>
              <h3>{t('fullChart.patient.ComplaintsHistoryAndOtherDetails')}</h3>
            </IonLabel>
          </IonRow>
        </IonCol>
      </IonItem>
      <IonItem onClick={onInitialAssessmentClick}>
        <IonCol className='ion-padding'>
          <IonRow>
            <IonLabel color='dark'>
              <h2>{t('fullChart.patient.initialAssessment.InitialAssessment')}</h2>
            </IonLabel>
          </IonRow>
          <IonRow>
            <IonLabel color='medium'>
              <h3>{t('fullChart.patient.initialAssessment.InitialAssessmentDetails')}</h3>
            </IonLabel>
          </IonRow>
        </IonCol>
      </IonItem>
      <IonItem onClick={onAdditionalAssessmentsClick}>
        <IonCol className='ion-padding'>
          <IonRow>
            <IonLabel color='dark'>
              <h2>{t('fullChart.patient.additionalAssessments.AdditionalAssessments')}</h2>
            </IonLabel>
          </IonRow>
          <IonRow>
            <IonLabel color='medium'>
              <h3>{t('fullChart.patient.additionalAssessments.AdditionalAssessmentsSubHeader')}</h3>
            </IonLabel>
          </IonRow>
        </IonCol>
      </IonItem>
      {shouldShowFluidsPage() && (
        <IonItem onClick={onFluidsClick}>
          <IonCol className='ion-padding'>
            <IonRow>
              <IonLabel color='dark'>
                <h2>{t('fullChart.patient.fluids.Fluids')}</h2>
              </IonLabel>
            </IonRow>
            <IonRow>
              <IonLabel color='medium'>
                <h3>{t('fullChart.patient.fluids.FluidsSubHeader')}</h3>
              </IonLabel>
            </IonRow>
          </IonCol>
        </IonItem>
      )}
      {hideComponent && (
        <IonItem onClick={showNotImplementedAlert}>
          <IonCol className='ion-padding'>
            <IonRow>
              <IonLabel color='dark'>
                <h2>{t('fullChart.patient.InjuryDetails')}</h2>
              </IonLabel>
            </IonRow>
            <IonRow>
              <IonLabel color='medium'>
                <h3>{t('fullChart.patient.CardiacArrestMVIExposureAndMore')}</h3>
              </IonLabel>
            </IonRow>
          </IonCol>
        </IonItem>
      )}
      {serviceConfig.PR_PG_NARRATIVE === 1 && (
        <IonItem onClick={onNarrativeClick}>
          <IonCol className='ion-padding'>
            <IonRow>
              <IonLabel color='dark'>
                <h2>{t('fullChart.patient.Narrative')}</h2>
              </IonLabel>
            </IonRow>
            <IonRow>
              <IonLabel color='medium'>
                <h3>{t('fullChart.patient.TreatmentAssessmentAndCOCDetails')}</h3>
              </IonLabel>
            </IonRow>
          </IonCol>
        </IonItem>
      )}
    </IonList>
  )
}

export default PatientList
